import { Link, useParams, useNavigate } from 'react-router-dom';
import { ChevronRight, OpenInNew, Description, ContentCopy, CheckCircle } from '@mui/icons-material';
import { Button, Card, CardContent, Chip, IconButton, Tooltip } from '@mui/material';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { LoadingState } from '../components/loading-state';
import { useGetProviderInfoQuery, useGetTemplateDetailsQuery } from '../store/api';

export default function TemplateDetailPage() {
  const { providerId, templateId, templateVersion } = useParams<{
    providerId: string;
    templateId: string;
    templateVersion: string;
  }>();
  const navigate = useNavigate();

  // Parse templateVersion and determine type from URL params
  // The type is passed as a query param in the URL
  const urlParams = new URLSearchParams(window.location.search);
  const templateType = (urlParams.get('type') || 'user') as 'public' | 'user';

  // Fetch provider info
  const { data: provider } = useGetProviderInfoQuery(
    { providerId: providerId || '' },
    { skip: !providerId },
  );

  // Fetch template details
  const { data: templateData, isLoading } = useGetTemplateDetailsQuery(
    {
      templateId: templateId || '',
      templateVersion: parseInt(templateVersion || '0', 10),
      type: templateType,
    },
    { skip: !templateId || !templateVersion },
  );

  if (!templateId || !templateVersion || !providerId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Invalid URL parameters</h2>
            <p className="text-gray-600 mt-2">Missing template ID, version, or provider ID.</p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingState message="Loading template details..." />
        </main>
      </div>
    );
  }

  if (!templateData?.data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Template not found</h2>
            <Link to={`/admin-v2/provider/${encodeURIComponent(providerId)}/templates`}>
              <Button className="mt-4">Back to Templates</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const template = templateData.data;
  const templateName =
    template._templateType === 'public'
      ? template.public_name || template.template_name
      : template.template_name;

  // Handle Edit Module button click
  const handleEditModule = (moduleId: string, sectionName: string) => {
    // Navigate to module edit page
    const moduleType = template._templateType || 'user';
    navigate(
      `/admin-v2/provider/${providerId}/templates/${templateId}/${templateVersion}/module-edit?moduleId=${moduleId}&moduleType=${moduleType}&templateType=${templateType}`
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton />
        </div>
        {/* Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/admin-v2">
                <Button
                  variant="text"
                  className="text-sm font-medium text-gray-700 hover:text-blue-600"
                >
                  <OpenInNew className="mr-2 h-4 w-4" />
                  Search
                </Button>
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <Link
                  to={`/admin-v2/provider/${encodeURIComponent(providerId)}`}
                  className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2"
                >
                  {provider?.name || 'Provider'}
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <Link
                  to={`/admin-v2/provider/${encodeURIComponent(providerId)}/templates`}
                  className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2"
                >
                  Templates
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">
                  {templateName || 'Template Detail'}
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Modern Template Header */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                  <Description className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-6">
                  <h1 className="text-2xl font-bold text-gray-900">{templateName || 'Unnamed Template'}</h1>
                  <p className="text-gray-600">Template for {provider?.name || 'Provider'}</p>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="text-sm text-gray-500">ID: {templateId}</span>
                    <span className="text-sm text-gray-500">Version: {templateVersion}</span>
                    <Chip
                      label={templateType}
                      size="small"
                      color={templateType === 'public' ? 'primary' : 'default'}
                    />
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">Total Sections</div>
                <div className="text-2xl font-bold text-gray-900">
                  {template.sections?.length || 0}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sections Table */}
        <Card className="mb-6">
          <CardContent>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Sections</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Section Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Module ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      In Summary
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {template.sections && template.sections.length > 0 ? (
                    template.sections.map((section, index) => {
                      const isInSummary = template.summary_modules?.includes(section.module_id);
                      return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {section.display_name}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-600 font-mono">
                                {section.module_id?.length > 30
                                  ? `${section.module_id.substring(0, 30)}...`
                                  : section.module_id}
                              </span>
                              {section.module_id && (
                                <Tooltip title="Copy Module ID">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      void navigator.clipboard.writeText(section.module_id);
                                    }}
                                  >
                                    <ContentCopy fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {isInSummary ? (
                              <Chip
                                label="In Summary"
                                size="small"
                                color="primary"
                                variant="outlined"
                                icon={<CheckCircle />}
                              />
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleEditModule(section.module_id, section.display_name)}
                            >
                              EDIT MODULE
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center">
                        <p className="text-gray-500">No sections defined for this template</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

      </main>
    </div>
  );
}